import {
  getWorkflowName,
  getWorkflowStatusValue,
  getWorkflowTriggerLabel,
  renderText,
} from './workflowLibraryUtils';

export const WORKFLOW_FILTER_FIELDS = [
  { value: 'workflowName', label: 'Workflow name', type: 'text', operators: ['contains', 'exists'] },
  { value: 'status', label: 'Status', type: 'select', operators: ['is', 'exists'], options: ['active', 'draft', 'paused', 'archived'] },
  { value: 'triggerType', label: 'Trigger type', type: 'text', operators: ['is', 'is_any_of', 'exists'] },
  { value: 'actionType', label: 'Action type', type: 'text', operators: ['is', 'is_any_of', 'exists'] },
  { value: 'updatedBy', label: 'Updated by', type: 'text', operators: ['is', 'is_any_of', 'exists'] },
  { value: 'tags', label: 'Tags', type: 'text', operators: ['is', 'is_any_of', 'exists'] },
  { value: 'createdOn', label: 'Created on', type: 'date', operators: ['is', 'exists'] },
  { value: 'updatedOn', label: 'Updated on', type: 'date', operators: ['is', 'exists'] },
];

export const DATE_VALUE_OPTIONS = [
  'Today',
  'Tomorrow',
  'Yesterday',
  'On',
  'After date',
  'Before date',
  'Between',
];

export function createDefaultFilter() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    field: '',
    operator: '',
    value: '',
    valueEnd: '',
    conjunction: 'AND',
  };
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function resolveDateRange(operator, value, valueEnd) {
  const now = new Date();
  if (operator === 'Today') {
    return { start: startOfDay(now), end: endOfDay(now) };
  }
  if (operator === 'Yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return { start: startOfDay(y), end: endOfDay(y) };
  }
  if (operator === 'Tomorrow') {
    const t = new Date(now);
    t.setDate(t.getDate() + 1);
    return { start: startOfDay(t), end: endOfDay(t) };
  }
  if (operator === 'On' && value) {
    const d = new Date(value);
    return { start: startOfDay(d), end: endOfDay(d) };
  }
  if (operator === 'After date' && value) {
    return { start: new Date(value), end: null };
  }
  if (operator === 'Before date' && value) {
    return { start: null, end: new Date(value) };
  }
  if (operator === 'Between' && value && valueEnd) {
    return { start: new Date(value), end: new Date(valueEnd) };
  }
  return null;
}

function matchesFilter(workflow, filter) {
  const { field, operator, value, valueEnd } = filter;
  if (!field || !operator) return true;

  const wfName = getWorkflowName(workflow).toLowerCase();
  const wfStatus = getWorkflowStatusValue(workflow);
  const wfTrigger = getWorkflowTriggerLabel(workflow).toLowerCase();
  const wfTags = Array.isArray(workflow.tags)
    ? workflow.tags.map((tag) => renderText(tag, '')).join(',').toLowerCase()
    : renderText(workflow.tags, '').toLowerCase();
  const wfUpdatedBy = renderText(workflow.updated_by || workflow.updatedBy, '').toLowerCase();
  const actionTypes = (workflow.steps || []).map((s) => renderText(s?.type, '')).join(',').toLowerCase();

  if (field === 'workflowName') {
    if (operator === 'exists') return Boolean(workflow.name);
    if (operator === 'contains') return wfName.includes(String(value || '').toLowerCase());
  }

  if (field === 'status') {
    if (operator === 'exists') return Boolean(wfStatus);
    if (operator === 'is') return wfStatus === String(value || '').toLowerCase();
  }

  if (field === 'triggerType') {
    if (operator === 'exists') return Boolean(wfTrigger);
    if (operator === 'is') return wfTrigger === String(value || '').toLowerCase();
    if (operator === 'is_any_of') {
      const parts = String(value || '').split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
      return parts.some((p) => wfTrigger.includes(p));
    }
  }

  if (field === 'actionType') {
    if (operator === 'exists') return actionTypes.length > 0;
    if (operator === 'is') return actionTypes.includes(String(value || '').toLowerCase());
    if (operator === 'is_any_of') {
      const parts = String(value || '').split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
      return parts.some((p) => actionTypes.includes(p));
    }
  }

  if (field === 'updatedBy') {
    if (operator === 'exists') return Boolean(wfUpdatedBy);
    if (operator === 'is') return wfUpdatedBy === String(value || '').toLowerCase();
    if (operator === 'is_any_of') {
      const parts = String(value || '').split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
      return parts.includes(wfUpdatedBy);
    }
  }

  if (field === 'tags') {
    if (operator === 'exists') return Boolean(wfTags);
    if (operator === 'is') return wfTags.includes(String(value || '').toLowerCase());
    if (operator === 'is_any_of') {
      const parts = String(value || '').split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
      return parts.some((p) => wfTags.includes(p));
    }
  }

  if (field === 'createdOn' || field === 'updatedOn') {
    const key = field === 'createdOn' ? 'created_at' : 'updated_at';
    const raw = workflow[key];
    if (operator === 'exists') return Boolean(raw);
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return false;
    const range = resolveDateRange(operator, value, valueEnd);
    if (!range) return true;
    if (range.start && date < range.start) return false;
    if (range.end && date > range.end) return false;
    return true;
  }

  return true;
}

export function applyWorkflowFilters(workflows, filters = [], searchQuery = '') {
  const list = Array.isArray(workflows) ? workflows : [];
  let result = [...list];

  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    result = result.filter((w) => (
      getWorkflowName(w).toLowerCase().includes(q)
      || renderText(w.description, '').toLowerCase().includes(q)
      || getWorkflowTriggerLabel(w).toLowerCase().includes(q)
    ));
  }

  const activeFilters = filters.filter((f) => f.field && f.operator);
  if (!activeFilters.length) return result;

  return result.filter((workflow) => activeFilters.every((filter) => matchesFilter(workflow, filter)));
}

export function validateFilter(filter) {
  if (!filter.field || !filter.operator) return null;
  const fieldConfig = WORKFLOW_FILTER_FIELDS.find((f) => f.value === filter.field);
  if (!fieldConfig) return null;

  if (filter.operator === 'exists') return null;

  if (fieldConfig.type === 'date') {
    const needsValue = !['Today', 'Tomorrow', 'Yesterday'].includes(filter.operator);
    if (needsValue && !filter.value) return 'Value cannot be empty';
    if (filter.operator === 'Between' && !filter.valueEnd) return 'Value cannot be empty';
    return null;
  }

  if (!filter.value) return 'Value cannot be empty';
  return null;
}

export function countActiveFilters(filters = []) {
  return filters.filter((f) => f.field && f.operator).length;
}
